// Material-UI
import { Typography, Grid, Theme, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import classNames from "classnames";

// Components
import { KeyboardKeyIcon } from "src/components/TimelineControls/KeyboardKeyIcon";
import { MouseIcon } from "src/components/TimelineControls/MouseIcon";
import AddIcon from "@mui/icons-material/Add";
import { IconButton } from "src/components/IconButton/IconButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface ICommand {
  action: string;
  command: string;
  keyCode?: string;
  mouseCode?: string;
}
interface ITimelineControls {
  controls?: ICommand[];
  setTabValue: React.Dispatch<React.SetStateAction<string | boolean>>;
}

const TimelineControlsStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexFlow: "row",
  "& .heading": {
    display: "contents",
  },
  "& .control": {
    display: "inline-flex",
    margin: "5px",
    padding: "5px",
    backgroundColor: theme.custom.forms.inputBack,
    borderRadius: "5px",
    alignItems: "center",
    "& .control_action": {
      fontSize: 12,
      maxWidth: 150,
      margin: "0px 8px",
    },
  },
  "& .control_action_command": {
    display: "flex",
    "& .add_icon": {
      color: theme.palette.primary.main,
    },
  },
});

const defaultControlsArr: ICommand[] = [
  {
    action: "Hold/Drag to Move Timeline",
    command: "Left Click",
    mouseCode: "0",
  },
  {
    action: "Select/De-select Events or Aircraft",
    command: "Left Click",
    mouseCode: "0",
  },
  {
    action: "Add/Remove to Selected Events",
    command: "Shift + Left Click",
    keyCode: "Shift",
    mouseCode: "0",
  },
  {
    action: "Open Context Menu (on event)",
    command: "Right Click Event",
    mouseCode: "1",
  },
  {
    action: "Cycle Day Forward (timeline in focus)",
    command: "Tab",
    keyCode: "Tab",
  },
  {
    action: "Cycle Day Back (timeline in focus)",
    command: "Shift + Tab",
    keyCode: "Shift + Tab",
  },
  {
    action: "Select Aircraft/Day (outside event)",
    command: "Right Click Day",
    mouseCode: "1",
  },
  { action: "Scroll Up/Down", command: "Scroll", mouseCode: "2" },
  {
    action: "Zoom In/Out",
    command: "Shift + Scroll",
    keyCode: "Shift",
    mouseCode: "2",
  },
];

/**
 * TIMELINE VIEW SCALE TOOL
 * Tool will display the nearest possible scale preset (when zooming with mouse and on mount)
 * Users can also click on button located below to jump to a selected timeline scale
 * For convenience, the center of the currently selected scale will be used for reference always
 * @param props - ITimelineControls
 */
export const TimelineControls = (props: ITimelineControls): JSX.Element => {
  const theme = useTheme();
  const { controls, setTabValue } = props;

  const content: ICommand[] = controls ? controls : defaultControlsArr;

  return (
    <Box sx={TimelineControlsStyles(theme)}>
      <Grid container className="control_action_command">
        <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
        {content.map((control: ICommand, index: number) => {
          const { keyCode, mouseCode, action } = control;
          const keyClass = keyCode ? "keyCode-" + keyCode : null;
          const mouseClass = mouseCode ? "mouseCode-" + mouseCode : null;

          return (
            <Grid item className={classNames("control", keyClass, mouseClass)} key={index}>
              {!!keyCode && KeyboardKeyIcon(keyCode)}
              {!!keyCode && !!mouseCode && <AddIcon className="add_icon" />}
              {!!mouseCode && MouseIcon(mouseCode)}
              {!!action && (
                <Typography variant="body2" className="control_action">
                  {action}
                </Typography>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
